/**
 * 30-%-Schwellen-Logik für „Fachkraft zur Mitarbeit" (FzM).
 *
 * FzM-Stellen zählen ihre vollen Wochenstunden als Fachkraftstunden,
 * aber die Summe der FzM-Stunden darf max. 30 % des Mindestpersonalbedarfs
 * nach HKJGB betragen (= ``fachkraftebedarfMin``). Die bewilligten
 * Integrationsstunden gehen dort nicht ein: Sie erhöhen den Soll, nicht
 * das gesetzliche Minimum — und damit auch nicht den Cap.
 *
 * Berechnung im Jahresdurchschnitt — eine Stelle, die nur von Juli bis
 * Dezember aktiv ist, beiträgt nur mit Faktor 6/12 zur Summe.
 */
import type { PersonalRow } from '@/api/types';
import { jahresanteil } from '@/lib/fachkraefte';

export const FZM_CAP_RATIO = 0.3;

export interface FzmWarn {
  /** Erste FzM-Stelle (Sort-Order-Reihenfolge), deren Beitrag das Limit
   *  reißt. */
  positionId: string;
  /** Std/Woche-Anzahl, die diese Stelle noch beitragen darf, ohne
   *  über das Limit zu kommen. ≥ 0, gerundet auf 2 Dezimalstellen. */
  restStdWoche: number;
}

/**
 * Klassifikation einer einzelnen FzM-Stelle bzgl. des 30-%-Caps:
 *
 *  - ``within``:   beitrag passt noch komplett in den Cap → grünes
 *                  „Ja (FzM)"
 *  - ``violator``: erste Stelle, deren Beitrag den Cap reißt → rotes
 *                  „Ja" + Rest-Std/W bis zum Cap
 *  - ``over``:     liegt nach der Verletzer-Stelle, beitrag = 0 → graues
 *                  „Nein (FzM)"
 */
export type FzmStatus = 'within' | 'violator' | 'over';

export interface FzmClassificationEntry {
  status: FzmStatus;
  /** Nur bei ``status='violator'`` gesetzt: Std/W bis zum Cap. */
  restStdWoche?: number;
}

/**
 * Klassifiziert jede FzM-Stelle (SuE, fzm=true) bzgl. des Caps. Reine
 * Fachkraft- und Ausbildungs-Stellen werden ignoriert und liefern
 * keinen Map-Eintrag.
 *
 * Reihenfolge: ``sort_order`` aufsteigend — neue Stellen landen
 * automatisch ans Ende (Backend setzt ``next = max + 1`` bei Anlage),
 * also deckt sich das mit der Eintragungs-Reihenfolge, solange der
 * User die Liste nicht aktiv umsortiert.
 */
export function classifyFzmRows(
  personal: ReadonlyArray<PersonalRow>,
  limit: number,
): Map<string, FzmClassificationEntry> {
  const out = new Map<string, FzmClassificationEntry>();
  const fzms = personal
    .filter((p) => p.tarifart === 'sue' && p.fzm)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  // Cap = 0 (oder negativ) → kein Belegungs-Snapshot vorhanden, jede FzM
  // wird als „nicht zulässig" markiert. Sicher konservativ.
  if (limit <= 0) {
    for (const p of fzms) out.set(p.id, { status: 'over' });
    return out;
  }
  let cumulative = 0;
  let violatorSeen = false;
  for (const p of fzms) {
    if (violatorSeen) {
      out.set(p.id, { status: 'over' });
      continue;
    }
    const beitrag = Number(p.std_woche || 0) * jahresanteil(p);
    if (cumulative + beitrag > limit) {
      const rest = Math.max(0, Math.round((limit - cumulative) * 100) / 100);
      out.set(p.id, { status: 'violator', restStdWoche: rest });
      violatorSeen = true;
    } else {
      out.set(p.id, { status: 'within' });
      cumulative += beitrag;
    }
  }
  return out;
}

/**
 * Convenience-Wrapper, der nur die erste Verletzer-Stelle liefert.
 * Wird in älterem Code für die simple „eine markierte Stelle"-Anzeige
 * genutzt; neue Aufrufer sollten direkt {@link classifyFzmRows} verwenden.
 */
export function findFzmThresholdViolation(
  personal: ReadonlyArray<PersonalRow>,
  limit: number,
): FzmWarn | null {
  for (const [positionId, entry] of classifyFzmRows(personal, limit)) {
    if (entry.status === 'violator') {
      return { positionId, restStdWoche: entry.restStdWoche ?? 0 };
    }
  }
  return null;
}
